import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./NewsFeaturedHero.module.css";
import CategoryChip from "../CategoryChip/CategoryChip";
import SlidePageIndicatorControl from "../SlidePageIndicatorControl/SlidePageIndicatorControl";
import ChevronSVG from "../SVGs/ChevronSVG";
import MindPageLink from "../MindPageLink/MindPageLink";

function NewsFeaturedHero({ featuredPosts }) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const diffX = useRef(0);
    const threshold = 50;
    const containerRef = useRef(null);

    const handleLeftButtonClick = useCallback(() => {
        setCurrentSlide((prev) => {
            if (prev === 0) {
                return featuredPosts?.length - 1;
            } else return prev - 1;
        });
    }, [featuredPosts?.length]);

    const handleRightButtonClick = useCallback(() => {
        setCurrentSlide((prev) => {
            return (prev + 1) % featuredPosts?.length;
        });
    }, [featuredPosts?.length]);

    const onSwipeLeft = useCallback(() => {
        handleLeftButtonClick();
    }, [handleLeftButtonClick]);

    const onSwipeRight = useCallback(() => {
        handleRightButtonClick();
    }, [handleRightButtonClick]);

    const handleMouseDown = useCallback((e) => {
        isDragging.current = true;
        startX.current = e.clientX;
        diffX.current = 0;
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging.current) return;
        diffX.current = e.clientX - startX.current;
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!isDragging.current) return;
        isDragging.current = false;

        if (Math.abs(diffX.current) > threshold) {
            diffX.current > 0 ? onSwipeRight?.() : onSwipeLeft?.();
        }
        diffX.current = 0;
    }, [onSwipeLeft, onSwipeRight]);

    const handleTouchStart = useCallback((e) => {
        startX.current = e.touches[0].clientX;
        diffX.current = 0;
    }, []);

    const handleTouchMove = useCallback((e) => {
        diffX.current = e.touches[0].clientX - startX.current;
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (Math.abs(diffX.current) > threshold) {
            diffX.current > 0 ? onSwipeRight?.() : onSwipeLeft?.();
        }
        diffX.current = 0;
    }, [onSwipeLeft, onSwipeRight]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener("mousedown", handleMouseDown);
        el.addEventListener("mousemove", handleMouseMove);
        el.addEventListener("mouseup", handleMouseUp);
        el.addEventListener("mouseleave", handleMouseUp);

        el.addEventListener("touchstart", handleTouchStart);
        el.addEventListener("touchmove", handleTouchMove);
        el.addEventListener("touchend", handleTouchEnd);

        return () => {
            el.removeEventListener("mousedown", handleMouseDown);
            el.removeEventListener("mousemove", handleMouseMove);
            el.removeEventListener("mouseup", handleMouseUp);
            el.removeEventListener("mouseleave", handleMouseUp);
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
        };
    }, [
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
    ]);

    return (
        <div id={styles.featured_hero_container} ref={containerRef}>
            {featuredPosts?.length > 0 ? (
                featuredPosts?.map((post, index) => (
                    <div
                        key={post.id}
                        className={[
                            styles.post_slide,
                            currentSlide === index ? styles.current_slide : "",
                        ].join(" ")}
                    >
                        <div
                            className={styles.background_image}
                            style={{
                                backgroundImage: `url(${post.featured_image_url})`,
                            }}
                        ></div>
                        <div className={styles.background_gradient}></div>
                        <MindPageLink
                            className={styles.link}
                            href={`/mind/news/${post.id}`}
                        >
                            <div className={styles.click_cover}></div>
                            <div className={styles.categories_container}>
                                {post.categories.map((cat) => (
                                    <CategoryChip
                                        {...cat}
                                        key={cat.id}
                                        removeLink={true}
                                    />
                                ))}
                            </div>
                            <h1>{post.name}</h1>
                        </MindPageLink>
                    </div>
                ))
            ) : (
                <div
                    className={["shimmer"].join(" ")}
                    id={styles.placeholder_slide}
                />
            )}
            <button
                id={styles.right_button}
                className={[styles.control, styles.side_button].join(" ")}
                onClick={handleRightButtonClick}
            >
                <ChevronSVG />
            </button>
            <button
                id={styles.left_button}
                className={[styles.control, styles.side_button].join(" ")}
                onClick={handleLeftButtonClick}
            >
                <ChevronSVG style={{ rotate: "180deg" }} />
            </button>
            <SlidePageIndicatorControl
                className={styles.control}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                totalSlides={featuredPosts?.length}
            />
        </div>
    );
}

export default NewsFeaturedHero;
